import hash from 'object-hash'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'

import type { CarouselProps } from '@hcr/ui'
import { CardEmbedment, CardVariant, CardPrimary, Carousel, unit } from '@hcr/ui'
import { flow, isNotNull } from '@hcr/utils'

import { SvgHoldingsNotVisible } from '../../../../../../assets/images'
import { useHoldingsQuery, useHoldingsNotVisibleQuery, useIdToken } from '../../../../../../hooks'
import { Path } from '../../../../../../models/navigation'
import { selectHoldingsWithNextWeeks } from '../../../../../../utils'
import { WarningMissingData } from '../../../../../common/warning-missing-data'
import { createHoldingPreview } from '../holding-preview'

import { HoldingsLoading } from './HoldingsLoading'

const holdingPreview = createHoldingPreview()

export const Holdings: FC = () => {
  const { t } = useTranslation()
  const idToken = useIdToken()

  const holdings = useHoldingsQuery(
    { idToken: String(idToken) },
    {
      select: flow(selectHoldingsWithNextWeeks),
      enabled: isNotNull(idToken),
      retryOnMount: false,
    }
  )
  const holdingsNotVisible = useHoldingsNotVisibleQuery({ idToken: String(idToken) })

  if (holdings.isError || holdingsNotVisible.isError) {
    return <WarningMissingData />
  }

  if (holdings.isSuccess && holdingsNotVisible.isSuccess) {
    const cardHoldingsNotVisible: CarouselProps['children'][number] = [
      `card-${hash([t('owner.cant-see-your-holding?')])}`,
      <CardPrimary
        badge={null}
        key={`card-${hash([t('owner.cant-see-your-holding?')])}`}
        description={holdingsNotVisible.data.ingress}
        embedment={CardEmbedment.Carousel}
        imageIcon={<SvgHoldingsNotVisible />}
        imageUrl={holdingsNotVisible.data.imageUrl}
        link={{
          isExternal: false,
          label: holdingsNotVisible.data.ctaText,
          to: Path.OwnerHoldingsNotVisible,
        }}
        title={holdingsNotVisible.data.title}
        variant={CardVariant.Regular}
      />,
    ]

    return (
      <Carousel slidesMargin={unit(3)} title={t('owner.your-holdings')}>
        {holdings.data.map(holdingPreview).concat([cardHoldingsNotVisible])}
      </Carousel>
    )
  }

  return <HoldingsLoading />
}
